// Materialization is explicit; mounting a saved banner must never generate new content.
type Color = {l: string; c: string; h: string};
const randomFloor = (min: number, max: number) => Math.min(Math.floor(min + Math.random() * (max - min + 1)), max);
export function hueDistance(h1, h2) {
  const d = Math.abs(h1 - h2);
  return Math.min(d, 360 - d);
}

/**
 * For each pair of palette colors whose hues are >120° apart (complements),
 * find the "bridge" hue — the palette color that sits between them.
 * When complements overlap, the further blob shifts toward the bridge.
 */
function buildColorMeta(oklchCols) {
  const hues = oklchCols.map((c) => parseFloat(c.h));
  const conflictPairs = [];

  for (let a = 0; a < hues.length; a++) {
    for (let b = a + 1; b < hues.length; b++) {
      if (hueDistance(hues[a], hues[b]) > 120) {
        // Find bridge: the palette color that is neither a nor b
        let bridgeHue = (hues[a] + hues[b]) / 2; // fallback: midpoint
        for (let k = 0; k < hues.length; k++) {
          if (k === a || k === b) continue;
          bridgeHue = hues[k];
          break;
        }
        conflictPairs.push({a, b, bridgeHue});
      }
    }
  }
  return {hues, conflictPairs};
}

const generatePositions = (gradientCount, stopCount) => {
  const positions = [];
  for (let i = 0; i < gradientCount; i++) {
    const stops = [];
    for (let s = 0; s < stopCount; s++) {
      const range = s === 0 ? [-70, 170] : [-50, 150];
      stops.push({x: randomFloor(range[0], range[1]), y: randomFloor(range[0], range[1])});
    }
    positions.push(stops);
  }
  return positions;
};

const generateColors = (count, palette) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    if (i < palette.length) {
      colors.push(palette[i]);
    } else {
      const idx = Math.floor(Math.random() * palette.length);
      const diff = randomFloor(-10, 10);
      const col = {...palette[idx], h: `${parseFloat(palette[idx].h) + diff}`};
      colors.push(col);
    }
  }
  return colors;
};

export function generateGenerativeBackground({productLabel, colors: oklchCols, count: generativeCount, stops: generativeStops, regenerateKey, animationId}: {
  productLabel: string; colors: Color[]; count: number; stops: number; regenerateKey: number; animationId: string;
}) {
  if (!oklchCols.length) throw new Error("No generative palette for this product");
  const key = `${productLabel}:${generativeCount}:${generativeStops}:${regenerateKey}`;
    const amount = generativeCount > 0 ? generativeCount : randomFloor(6, 15);
    const stopCount = generativeStops;
    const colors = generateColors(amount, oklchCols);
    const positions = generatePositions(amount, stopCount);

    // Assign each blob to its closest palette color (for conflict checking)
    const assignments = colors.map((color) => {
      const h = parseFloat(color.h);
      let closest = 0;
      let minDist = Infinity;
      for (let p = 0; p < oklchCols.length; p++) {
        const d = hueDistance(h, parseFloat(oklchCols[p].h));
        if (d < minDist) {
          minDist = d;
          closest = p;
        }
      }
      return closest;
    });

    const colorMeta = buildColorMeta(oklchCols);
    const originalHues = colors.map((c) => parseFloat(c.h));

    const gradients = [];
    const cssProperties: Record<string, string> = {};
    const animName = `move-${animationId}`;

    const stopPercentages = Array.from({length: stopCount}, (_, s) =>
      Math.round((s / (stopCount - 1)) * 100)
    );
    const keyframeBlocks = stopPercentages.map(() => "");

    for (let i = 0; i < amount; i++) {
      const idx = i + 1;
      const stops = positions[i];
      const {l, c, h} = colors[i];
      const hueVal = parseFloat(h);

      cssProperties[`--x${idx}`] = `${stops[0].x}%`;
      cssProperties[`--y${idx}`] = `${stops[0].y}%`;
      cssProperties[`--h${idx}`] = `${hueVal}`;

      for (let s = 0; s < stopCount; s++) {
        keyframeBlocks[s] += `    --x${idx}: ${stops[s].x}%; --y${idx}: ${stops[s].y}%;\n`;
      }

      // Use var(--h{idx}) for hue so the animation loop can shift it at runtime
      gradients.push(
        `radial-gradient(circle at var(--x${idx}) var(--y${idx}), oklch(${l} ${c} var(--h${idx}) / 80%) 0px, oklch(${l} ${c} var(--h${idx}) / 60%) 40%, transparent 70%)`
      );
    }

    const keyframesStr = `@keyframes ${animName} {\n${stopPercentages
      .map((pct, s) => `  ${pct}% {\n${keyframeBlocks[s]}  }`)
      .join("\n")}\n}`;

    return {
      paletteKey: key,
      gradients,
      cssProperties,
      keyframes: keyframesStr,
      animationName: animName,
      positions,
      colorMeta: {
        assignments,
        originalHues,
        conflictPairs: colorMeta.conflictPairs,
      },
    };
}
